import type { Incident } from '../../services/incidentsApi';

function safeText(value: unknown): string {
  return String(value || '').trim();
}

/** Edgentik pattern owner (group action assignee on matching open linked records). */
export function readPatternOwnerTeamLabel(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  return safeText(
    (incident as any)?.canonical_pattern_owner_team
    || (incident as any)?.pattern_owner_team
    || (incident as any)?.safety_case?.ownership?.pattern_owner_team,
  );
}

export function readPatternAssignedByLabel(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  return safeText(
    (incident as any)?.canonical_pattern_assigned_by
    || (incident as any)?.pattern_assigned_by
    || (incident as any)?.safety_case?.ownership?.pattern_assigned_by,
  );
}

export function readPatternAssignedAtLabel(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  return safeText(
    (incident as any)?.canonical_pattern_assigned_at
    || (incident as any)?.pattern_assigned_at,
  );
}

export function readActionAssignedByLabel(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  return safeText(
    (incident as any)?.safety_case?.ownership?.assigned_by
    || (incident as any)?.assigned_by,
  );
}

export function readPatternOwnerLabel(incident: Incident | Record<string, any> | null | undefined): string {
  const canonical = safeText(
    (incident as any)?.canonical_pattern_owner
    || (incident as any)?.pattern_owner_display
    || (incident as any)?.pattern_owner,
  );
  if (canonical) return canonical;
  const ownership = (incident as any)?.safety_case?.ownership;
  if (ownership && typeof ownership === 'object') {
    const fromSafetyCase = safeText(ownership.pattern_owner);
    if (fromSafetyCase) return fromSafetyCase;
    return '';
  }
  return safeText(
    (incident as any)?.assignee
    || (incident as any)?.assigned_to,
  );
}

/** Edgentik follow-up owner for representative evidence workflow. */
export function readFollowupOwnerLabel(incident: Incident | Record<string, any> | null | undefined): string {
  return safeText(
    (incident as any)?.followup_owner_display
    || (incident as any)?.followup_owner
    || (incident as any)?.safety_case?.ownership?.followup_owner
    || (incident as any)?.safety_case?.action_snapshot?.owner
    || (incident as any)?.safety_case_finding?.followup_owner
    || (incident as any)?.assignee
    || (incident as any)?.assigned_to,
  );
}

export function readRuleOwnerLabel(incident: Incident | Record<string, any> | null | undefined): string {
  return safeText(
    (incident as any)?.safety_case?.ownership?.rule_owner
    || (incident as any)?.taxonomy?.owner
    || (incident as any)?.owner,
  );
}

export const PATTERN_ASSIGN_BACKEND_NOTE =
  'Canonical owner for this recurring risk pattern. Assigning a risk owner updates the recurring-risk record and applies the owner to matching open linked records in the selected window.';

export const FOLLOWUP_SAVE_BACKEND_NOTE =
  'Saves follow-up owner and next review on this review item only. Does not change risk owner.';

/** Patch local incident/group rows after a pattern-owner assign so the open review UI updates immediately. */
export function applyLocalPatternOwnerAssignment<T extends Record<string, any>>(
  row: T | null | undefined,
  owner: string | null | undefined,
  team?: string | null,
): T | null {
  if (!row) return null;
  const nextOwner = safeText(owner);
  if (!nextOwner) return row;
  const nextTeam = team == null ? safeText((row as any).pattern_owner_team) : safeText(team);
  const ownership = ((row as any).safety_case?.ownership && typeof (row as any).safety_case.ownership === 'object')
    ? { ...(row as any).safety_case.ownership }
    : {};
  ownership.pattern_owner = nextOwner;
  if (team != null) ownership.pattern_owner_team = nextTeam || null;

  const next: Record<string, any> = {
    ...row,
    pattern_owner: nextOwner,
    pattern_owner_display: nextOwner,
    canonical_pattern_owner: nextOwner,
  };
  if (team != null) {
    next.pattern_owner_team = nextTeam || null;
    next.canonical_pattern_owner_team = nextTeam || null;
  }
  if ((row as any).safety_case && typeof (row as any).safety_case === 'object') {
    next.safety_case = {
      ...(row as any).safety_case,
      ownership,
    };
  }
  return next as T;
}
